import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../../app/api';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { user } from '../../../store/reducers/user';
import _ from 'lodash';
import MessageBox from '../../../util/messageBox';
import uuidv4 from 'uuid/v4';
import { patientBanner } from '../tasks/components/patientBanner';
import useOpenState from '../../../hooks/useOpenState';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleDictionaryField from '../../../components/ui/selector/agyleDictionaryField';
import NewNoteModal from '../../../components/notes/component/newNoteModal';
import useDict from '../../../hooks/useDict';
import { specialties as specialtiesReducer } from '../../../store/reducers/preferences';
import MiniFormDialog from '../../../components/modal/miniFormDialog';
import { DialogSeenAndWhenExt } from './dialogSeenAndWhenExt';
import { useClinicianStyles } from './useClinicianStyles';
import { MedicalNursePlanInput } from './medicalNursePlanInput';
import { getConfig } from '../../../util/configUtil';

function DialogSeen(props) {
    const { t } = useTranslation();
    const userObject = useSelector(user);
    const classes = useClinicianStyles();

    const [open, setOpen] = React.useState(props.open || false);
    const [seen, setSeen] = React.useState(props.value);
    const [seenDefault, setSeenDefault] = React.useState(props.value);
    const [isError, setIsError] = useState(false);
    const [errorPicker, setErrorPicker] = useState(null);
    const [formIsModified, setFormIsModified] = React.useState(props.isActionMode);
    const [text, setText] = React.useState(props.value?.text || '');
    const [speciality, setSpeciality] = useState(null);
    const [medicalNursingNotes, setMedicalNursingNotes] = React.useState(props.value?.notes || []);
    const [addBtnSeen, setAddBtnSeen] = React.useState(true);
    const initNote = { id: uuidv4(), text: '', checked: false, checkedBy: null };

    const [initialNote, setInitialNote] = React.useState(initNote);

    const [openConfirmDeleted, setOpenConfirmDeleted] = React.useState(false);
    const [isClinicalNotesOpen, openClinicalNotes, closeClinicalNotes] = useOpenState(false, true);
    const [dictSpecialties, dictSpecialtiesLoading] = useDict('Specialties', '__description', `relevent eq true`);

    const specialties = useSelector(specialtiesReducer);

    // Configs
    const defaultedClinician = getConfig('clinicianAndDateDefaultedToCurrentClinician', false);

    useEffect(() => {
        if (open && !props.value?.who && !props.isActionMode && !formIsModified) {
            setFormIsModified(true);
        }
    }, [open, props.isActionMode, props.value]);

    useEffect(() => {
        const seenNeeded = props.isActionMode ? false : !seen?.who;
        setIsError(Boolean(seenNeeded || !seen?.when || !_.isNull(errorPicker)));
    }, [seen, errorPicker, props.isActionMode]);

    const handleClose = (_e, reason) => {
        if (reason !== 'backdropClick') {
            setOpen(false);
            setFormIsModified(false);
            if (props.onClose) {
                props.onClose();
            }
        }
    };

    const handleClear = () => {
        props.withNotes
            ? props.onChange({
                  who: null,
                  when: null,
                  timestamp: null,
                  notes: [],
              })
            : props.onChange({
                  who: null,
                  when: null,
                  timestamp: null,
                  text: '',
              });
        setOpenConfirmDeleted(false);
        setOpen(false);
        setFormIsModified(false);
    };

    const valid = () => {
        const seenNeeded = props.isActionMode ? true : seen.who;
        return seenNeeded && seen?.when && !errorPicker;
    };

    const sendChange = () => {
        if (valid()) {
            let seenClone = { ...seen, timestamp: moment().toISOString() };
            if (props.withText && !props.withNotes) {
                seenClone = { ...seenClone, text: text };
            }
            if (props.withNotes) {
                seenClone = { ...seenClone, notes: medicalNursingNotes };
            }
            if (props.withSpeciality) {
                seenClone = { ...seenClone, speciality: speciality };
            }
            props.onChange(seenClone);
            handleClose();
        }
    };

    useEffect(() => {
        let isMounted = true;
        let clinicianChanged = false;

        if (!props.ambulanceHandover) {
            if (!props.nurse || props.useNurseAndClinicianDict) {
                API.getDictByURL(`/$dict/Clinicians`).then((dictData) => {
                    if (!isMounted) return;

                    if (dictData) {
                        for (let clinitian of dictData.__data) {
                            if (clinitian.userName === userObject.userName) {
                                setSeenDefault({
                                    who: clinitian,
                                    when: defaultedClinician ? moment().toISOString() : seen?.when,
                                });
                                if (!seen?.who || props.planForm || defaultedClinician) {
                                    setSeen((prevValue) => ({
                                        ...prevValue,
                                        who: clinitian,
                                        when: defaultedClinician ? moment().toISOString() : seenDefault?.when,
                                    }));
                                }
                                clinicianChanged = true;
                                break;
                            }
                        }
                    }
                });
            }

            if (props.nurse) {
                API.getDictByURL(`/$dict/Nurses`).then((dictData) => {
                    if (!isMounted) {
                        return;
                    }

                    if (dictData) {
                        for (let clinitian of dictData.__data) {
                            if (clinitian.userName === userObject.userName) {
                                setSeenDefault({
                                    who: clinitian,
                                    when: defaultedClinician ? moment().toISOString() : seen?.when,
                                });
                                if (!seen?.who || defaultedClinician) {
                                    setSeen((prevValue) => ({
                                        ...prevValue,
                                        who: clinitian,
                                        when: defaultedClinician ? moment().toISOString() : seenDefault?.when,
                                    }));
                                }
                                clinicianChanged = true;
                                break;
                            }
                        }
                    }
                });
            }

            if (props.defaultClinician) {
                API.getDictByURL(`/$dict/Clinicians`).then((dictData) => {
                    if (!dictData || !dictData.__data) return;

                    const match = dictData.__data.find((c) => c.__description === props.defaultClinician);

                    if (match) {
                        setSeen((prev) => ({
                            ...prev,
                            who: match,
                            when: moment().toISOString(),
                        }));
                    }
                });
            }

            if (!clinicianChanged && defaultedClinician) {
                setSeen((prevState) => ({
                    ...prevState,
                    when: moment().toISOString(),
                    who: null,
                }));

                setSeenDefault((prevState) => ({
                    ...prevState,
                    when: moment().toISOString(),
                    who: null,
                }));
            }
        }

        return () => {
            isMounted = false;
        };
    }, [open, props.defaultClinician]);

    useEffect(() => {
        if (props.withSpeciality) {
            specialties.forEach((s) => {
                if ('isActive' in s)
                    if (s.isActive) {
                        setSpeciality(s);
                    }
            });
        }
    }, [specialties, open]);

    useEffect(() => {
        if (!open) {
            if (props.openRemote) {
                setOpen(true);
            }
        }
    }, [props.openRemote]);

    const checkIsNursingOrMedicalPlan = useCallback(
        () => props.type === 'studies.medicalPlan' || props.type === 'studies.nursingPlanNote' || props.type === 'studies.treatmentPlan',
        [props.type],
    );

    const isTreatmentOrNursingPlan = useCallback(
        () => props.type === 'studies.nursingPlanNote' || props.type === 'studies.treatmentPlan',
        [props.type],
    );

    const getButtons = () => {
        const buttons = [];
        if (props.noteButton) buttons.push(<AgyleButton onClick={openClinicalNotes}>{t('Add note')}</AgyleButton>);
        if (!props.disableSave && !props.defaultClinician)
            buttons.push(<AgyleButton onClick={() => setOpenConfirmDeleted(true)}>{t('Delete')}</AgyleButton>);

        return buttons;
    };

    return (
        <>
            {props.buttonLabel && (
                <Grid container spacing={2}>
                    <Grid item xs={12} disabled={props.disabled} size={props.size} style={{ width: '100%', textAlign: 'center' }}>
                        <div
                            onClick={() => {
                                if (!props.disableOpen) {
                                    if (!props.value || !props.value?.who || !props.value?.when) {
                                        setSeen({
                                            who: seenDefault?.who || props.patient?.currentClinician,
                                            when: moment().toISOString(),
                                        });
                                    } else {
                                        setSeen(props.value);
                                    }
                                    setText(props.value?.text ? props.value.text : '');
                                    if (seenDefault && props.withSpeciality)
                                        setSpeciality(props.value?.who?.__id === seenDefault?.who?.__id ? props.value.speciality : null);
                                    setOpen(true);
                                }
                            }}
                            style={{ cursor: props.disableOpen ? 'auto' : 'pointer' }}
                        >
                            {props.buttonLabel(props.value)}
                        </div>
                        {/*</Button>*/}
                    </Grid>
                </Grid>
            )}

            <MiniFormDialog
                open={open}
                onClose={handleClose}
                onSave={sendChange}
                disableSave={props.disableSave}
                title={`Add ${props?.title || props?.label}`}
                buttons={getButtons()}
                closeButtonOnHeader
                width="sm"
                fullWidth
                disableSubmit={isError || (checkIsNursingOrMedicalPlan() ? _.isEmpty(medicalNursingNotes) : false)}
                formOptions={props?._formOptions}
            >
                <Grid container>
                    {props.isActionMode && !props.fromPatientScreen && patientBanner(props.patient, true)}

                    {checkIsNursingOrMedicalPlan() && (
                        <MedicalNursePlanInput
                            setFormIsModified={setFormIsModified}
                            medicalNursingNotes={medicalNursingNotes}
                            setMedicalNursingNotes={setMedicalNursingNotes}
                            addBtnSeen={addBtnSeen}
                            setAddBtnSeen={setAddBtnSeen}
                            seenDefault={seenDefault}
                            user={userObject}
                            initialNote={initialNote}
                            setInitialNote={setInitialNote}
                            initNote={initNote}
                            seen={seen}
                            fromTimestamp={props.isActionMode}
                        />
                    )}

                    <DialogSeenAndWhenExt
                        patient={props.patient}
                        nurse={props.nurse}
                        cliniciansWithNurses={isTreatmentOrNursingPlan()}
                        size="small"
                        label={props.label}
                        value={seen}
                        allowedConsultants={props.allowedConsultants}
                        onChange={(value) => {
                            setSeen(value);
                            setFormIsModified(true);
                            if (props.withSpeciality) {
                                setSpeciality(null);
                            }
                        }}
                        error={isError}
                        onError={setErrorPicker}
                        admittedDate={props.ambulanceHandover}
                        disableDateManipulation={props.disableDateManipulation}
                        refresh={props.refresh}
                        fromTimestamp={props.isActionMode}
                    />

                    {props.withText && !checkIsNursingOrMedicalPlan() && (
                        <div className={classes.noteField}>
                            <AgyleTextField
                                size={props.refresh ? 'large' : 'small'}
                                className={'custom-dt'}
                                placeholder={t('Enter note')}
                                fullWidth
                                label={t('Note')}
                                onChange={(e) => {
                                    setText(e.target.value);
                                    setFormIsModified(true);
                                }}
                                value={text}
                                multiline
                                noBorder={!props.refresh}
                                withNewBackground={props.refresh}
                            />
                        </div>
                    )}
                    {props.withSpeciality && (
                        <div className={classes.noteField}>
                            <AgyleDictionaryField
                                getOptionLabel={(option) => option.__description}
                                options={dictSpecialties}
                                label={t('Speciality')}
                                loading={dictSpecialtiesLoading}
                                value={speciality}
                                isOptionEqualToValue={(option, value) => option.__id === value.__id}
                                onChange={(_e, newValue) => {
                                    setSpeciality(newValue);
                                }}
                            />
                        </div>
                    )}
                </Grid>
            </MiniFormDialog>

            <MessageBox
                title={t('CAUTION')}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                onAccept={handleClear}
                onClose={() => setOpenConfirmDeleted(false)}
                message={t('Are you sure you want delete all recorded values?')}
                open={openConfirmDeleted}
            />

            <NewNoteModal
                open={isClinicalNotesOpen}
                onClose={closeClinicalNotes}
                patient={props.patient}
                disableSave={props.disableSave}
                fromForm={props.noteButtonType}
            />
        </>
    );
}

export { DialogSeen };
